import { NavLink, Outlet, useNavigate, useLocation, Link, useParams, Navigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  Package, 
  Users, 
  FileText,
  Menu,
  X,
  Sun,
  Moon,
  LogOut,
  ArrowLeft,
  Settings,
  User,
  Building2,
  Receipt,
  Upload,
  ShoppingCart,
  Barcode,
  Activity,
  Globe,
  PieChart,
  CreditCard,
  Droplets,
  MapPin,
  Send,
  ClipboardList,
} from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import SemiTruck from "@/components/icons/SemiTruck";
import MechanicTools from "@/components/icons/MechanicTools";
import ClassicSemiTruck from "@/components/icons/ClassicSemiTruck";
import SemiTrailer from "@/components/icons/SemiTrailer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon is now database-driven via businessInfo.icon field

export const Layout = ({ children }) => {
  const { businessSlug } = useParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [businessInfo, setBusinessInfo] = useState(null);
  const { theme, toggleTheme } = useTheme();
  const { logout, user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get business category from settings
  const [businessCategory, setBusinessCategory] = useState(null);
  
  // ALL useEffects must be before any conditional returns
  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const res = await axios.get(`${API}/b/${businessSlug}/settings`);
        setBusinessCategory(res.data?.category);
      } catch (err) {
        // Default to mechanic if can't fetch
      }
    };
    fetchCategory();
  }, [businessSlug]);

  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await axios.get(`${API}/b/${businessSlug}/info`);
        setBusinessInfo(response.data);
      } catch (error) {
        console.error("Failed to fetch business info", error);
      }
    };
    if (businessSlug) {
      fetchBusinessInfo();
    }
  }, [businessSlug]);
  
  const isDark = theme === "dark";
  const basePath = `/${businessSlug}`;
  const showBackButton = location.pathname !== basePath && !location.pathname.includes('/grocery');
  
  // BLOCK WHOLESALE/GROCERY USERS from the mechanic dashboard.
  // Only redirect if they're NOT already on their own category's dashboard
  // OR on a shared page (settings/profile), otherwise GroceryDashboard /
  // WholesaleDashboard (which both wrap in this Layout) cause an infinite
  // Navigate→Navigate redirect loop, AND Settings/Profile become unreachable.
  // This check must come AFTER all hooks
  const userCategory = user?.business_category;
  const onWholesale = location.pathname.includes('/wholesale');
  const onGrocery = location.pathname.includes('/grocery');
  const onPressureWashing = location.pathname.includes('/pressure-washing');
  const onSharedPage =
    location.pathname.includes('/settings') ||
    location.pathname.includes('/profile');
  if (userCategory === 'wholesale' && !onWholesale && !onSharedPage) {
    return <Navigate to={`/${user?.business_slug}/wholesale`} replace />;
  }
  if (userCategory === 'grocery_store' && !onGrocery && !onSharedPage) {
    return <Navigate to={`/${user?.business_slug}/grocery`} replace />;
  }
  if (userCategory === 'pressure_washing' && !onPressureWashing && !onSharedPage) {
    return <Navigate to={`/${user?.business_slug}/pressure-washing`} replace />;
  }
  
  // Icon is database-driven or category-based
  const getBusinessIcon = () => {
    // For grocery stores, use ShoppingCart
    if (businessCategory === 'grocery_store') return ShoppingCart;
    // For wholesale, use Globe
    if (businessCategory === 'wholesale') return Globe;
    // For pressure washing, use Droplets
    if (businessCategory === 'pressure_washing') return Droplets;
    
    const icon = businessInfo?.icon;
    if (icon === 'tools') return MechanicTools;
    if (icon === 'truck') return SemiTruck;
    if (icon === 'classic-truck') return ClassicSemiTruck;
    if (icon === 'semi-trailer') return SemiTrailer;
    return SemiTruck; // default
  };

  // Get the business icon (must be after businessCategory is available)
  const BusinessIcon = getBusinessIcon();

  // Navigation items based on business category
  const getNavItems = () => {
    if (businessCategory === 'grocery_store') {
      return [
      { path: `${basePath}/grocery`, icon: LayoutDashboard, label: "DASHBOARD", end: true },
        { path: `${basePath}/grocery?tab=register`, icon: CreditCard, label: "REGISTER" },
        { path: `${basePath}/grocery?tab=tax`, icon: Receipt, label: "TAX MONITOR" },
        { path: `${basePath}/grocery?tab=inventory`, icon: Package, label: "INVENTORY" },
        { path: `${basePath}/grocery?tab=transactions`, icon: FileText, label: "TRANSACTIONS" },
        { path: `${basePath}/grocery?tab=returns`, icon: Upload, label: "RETURNS" },
        { path: `${basePath}/grocery?tab=settings`, icon: Settings, label: "SETTINGS" },
      ];
    }
    if (businessCategory === 'wholesale') {
      return [
        { path: `${basePath}/wholesale`, icon: Activity, label: "DASHBOARD", end: true },
        { path: `${basePath}/wholesale?tab=shipments`, icon: Package, label: "SHIPMENTS" },
        { path: `${basePath}/wholesale?tab=inventory`, icon: Barcode, label: "INVENTORY" },
        { path: `${basePath}/wholesale?tab=invoices`, icon: FileText, label: "INVOICES" },
        { path: `${basePath}/settings`, icon: Settings, label: "SETTINGS" },
      ];
    }
    if (businessCategory === 'pressure_washing') {
      return [
        { path: `${basePath}/pressure-washing`, icon: LayoutDashboard, label: "DASHBOARD", end: true },
        { path: `${basePath}/pressure-washing?tab=quotes`, icon: MapPin, label: "AERIAL QUOTES" },
        { path: `${basePath}/pressure-washing?tab=jobs`, icon: ClipboardList, label: "JOBS" },
        { path: `${basePath}/pressure-washing?tab=customers`, icon: Users, label: "CUSTOMERS" },
        { path: `${basePath}/pressure-washing?tab=rates`, icon: Droplets, label: "SURFACE RATES" },
        { path: `${basePath}/pressure-washing?tab=invoices`, icon: FileText, label: "INVOICES" },
        { path: `${basePath}/settings`, icon: Settings, label: "SETTINGS" },
      ];
    }
    // Default mechanic/service items
    return [
      { path: basePath, icon: LayoutDashboard, label: "DASHBOARD", end: true },
      { path: `${basePath}/inventory`, icon: Package, label: "INVENTORY" },
      { path: `${basePath}/customers`, icon: Users, label: "CUSTOMERS" },
      { path: `${basePath}/invoices`, icon: FileText, label: "INVOICES" },
      { path: `${basePath}/financials`, icon: PieChart, label: "FINANCIALS" },
      { path: `${basePath}/payments`, icon: CreditCard, label: "PAYMENTS" },
      // Tax Reports + Import Data moved out of the side nav per user
      // request — Tax Reports is now reached from the Financials page
      // ("Need full tax report?"), and Import Data is split into
      // contextual buttons on the Inventory + Customers pages.
      { path: `${basePath}/settings`, icon: Settings, label: "SETTINGS" },
    ];
  };
  
  const navItems = getNavItems();
  const isGrocery = businessCategory === 'grocery_store';
  const isWholesale = businessCategory === 'wholesale';

  // Custom active-state check that respects ?tab= query params.
  // NavLink's default `isActive` only looks at the pathname, so all six grocery
  // nav items (which share the same `/grocery` path and only differ by ?tab=)
  // would light up at once. This compares pathname AND tab query.
  const currentPath = location.pathname;
  const currentTab = new URLSearchParams(location.search).get('tab');
  const isItemActive = (itemPath, isEnd) => {
    const [iPath, iQuery] = itemPath.split('?');
    const itemTab = iQuery ? new URLSearchParams(iQuery).get('tab') : null;
    if (iPath !== currentPath) return false;
    // Item with no tab param is the "base" view; active only when no tab is set
    if (!itemTab) return isEnd ? !currentTab : true;
    return itemTab === currentTab;
  };

  const handleLogout = () => {
    logout();
    window.location.href = "/login";
  };

  const displayName = businessInfo?.name || businessSlug?.toUpperCase() || "BUSINESS";
  const shortName = displayName.split(' ')[0].toUpperCase();

  return (
    <div className={`flex min-h-screen ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex flex-col w-60 border-r fixed h-screen ${isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-white'}`}>
        <div className={`px-5 py-4 border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-md p-1 ${isDark ? 'bg-slate-700' : 'bg-white border border-slate-200'}`}>
              {businessInfo?.logo_url ? (
                <img src={businessInfo.logo_url} alt={displayName} className="w-full h-full object-contain" />
              ) : (
                <BusinessIcon className={`w-6 h-6 ${isGrocery ? (isDark ? 'text-cyan-400' : 'text-cyan-600') : (isDark ? 'text-emerald-400' : 'text-emerald-600')}`} />
              )}
            </div>
            <div className="min-w-0">
              <h1 className={`font-semibold text-[15px] tracking-tight truncate ${isDark ? 'text-white' : 'text-slate-800'}`}>
                {shortName}
              </h1>
              <p className={`text-[10px] uppercase tracking-widest truncate ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {businessInfo?.phone || 'Dashboard'}
              </p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-0.5">
            {navItems.map((item) => {
              const active = isItemActive(item.path, item.end);
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium tracking-wide transition-all duration-200 rounded-lg ${
                      active
                        ? (isGrocery
                          ? (isDark ? 'text-white bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30' : 'text-cyan-700 bg-cyan-50 border border-cyan-200')
                          : (isDark ? 'text-emerald-400 bg-emerald-500/10' : 'text-emerald-600 bg-emerald-50'))
                        : `${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800/40' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`
                    }`}
                  >
                    <item.icon className="w-[18px] h-[18px]" />
                    {item.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className={`px-3 py-3 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          {/* Super Admin Link */}
          {isSuperAdmin && (
            <Link to="/admin">
              <Button
                variant="ghost"
                size="sm"
                className={`w-full mb-1 justify-start gap-3 h-9 text-[13px] text-purple-500 ${isDark ? 'hover:bg-slate-800' : 'hover:bg-purple-50'}`}
                data-testid="admin-link"
              >
                <Building2 className="w-4 h-4" />
                Admin Panel
              </Button>
            </Link>
          )}
          
          {/* Profile Link */}
          <Link to={`${basePath}/profile`}>
            <Button
              variant="ghost"
              size="sm"
              className={`w-full mb-1 justify-start gap-3 h-9 text-[13px] ${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}
              data-testid="profile-link"
            >
              <User className="w-4 h-4" />
              Profile
            </Button>
          </Link>
          
          {/* Theme Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleTheme}
            className={`w-full mb-1 justify-start gap-3 h-9 text-[13px] ${isDark ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}
            data-testid="theme-toggle"
          >
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            {isDark ? "Light Mode" : "Dark Mode"}
          </Button>
          
          {/* Logout */}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className={`w-full justify-start gap-3 h-9 text-[13px] text-red-500 hover:text-red-600 ${isDark ? 'hover:bg-slate-800' : 'hover:bg-red-50'}`}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Log Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className={`lg:hidden fixed top-0 left-0 right-0 z-50 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'} border-b`}>
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            {showBackButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className={`mr-2 ${isDark ? 'text-white hover:bg-slate-800' : 'text-slate-700 hover:bg-slate-100'}`}
                data-testid="back-btn-mobile"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center p-1 shadow-sm">
              {businessInfo?.logo_url ? (
                <img src={businessInfo.logo_url} alt={displayName} className="w-full h-full object-contain" />
              ) : (
                <BusinessIcon className="w-6 h-6 text-blue-600" />
              )}
            </div>
            <span className={`font-bold uppercase tracking-tight ${isDark ? 'text-white' : 'text-slate-800'}`}>
              {shortName}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className={isDark ? 'text-slate-300' : 'text-slate-600'}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className={`p-3 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-slate-50 border-slate-200'} border-t`}>
            <ul className="space-y-0.5">
              {navItems.map((item) => {
                const active = isItemActive(item.path, item.end);
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium tracking-wide transition-colors duration-200 rounded-lg ${
                        active
                          ? (isGrocery
                              ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                              : 'bg-blue-600 text-white')
                          : `${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800/50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`
                      }`}
                    >
                      <item.icon className="w-[18px] h-[18px]" />
                      {item.label}
                    </NavLink>
                  </li>
                );
              })}
              {isSuperAdmin && (
                <li>
                  <NavLink
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium tracking-wide text-purple-500 hover:bg-purple-50 dark:hover:bg-slate-800/50 rounded-lg"
                  >
                    <Building2 className="w-[18px] h-[18px]" />
                    ADMIN PANEL
                  </NavLink>
                </li>
              )}
              <li>
                <NavLink
                  to={`${basePath}/profile`}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium tracking-wide transition-colors duration-200 rounded-lg ${
                      isActive
                        ? "bg-blue-600 text-white"
                        : `${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800/50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`
                    }`
                  }
                >
                  <User className="w-[18px] h-[18px]" />
                  PROFILE
                </NavLink>
              </li>
              <li>
                <button
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium tracking-wide text-red-500 hover:bg-red-50 dark:hover:bg-slate-800/50 rounded-lg w-full"
                >
                  <LogOut className="w-[18px] h-[18px]" />
                  LOG OUT
                </button>
              </li>
            </ul>
          </nav>
        )}
      </div>

      {/* Main Content */}
      <main className={`flex-1 lg:p-8 p-4 pt-20 lg:pt-8 lg:ml-60 overflow-auto ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}>
        {/* Desktop Back Button */}
        {showBackButton && (
          <div className="hidden lg:block mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className={`gap-2 ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'}`}
              data-testid="back-btn-desktop"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
          </div>
        )}
        {children || <Outlet />}
      </main>
    </div>
  );
};

export default Layout;
